class LaasaShoppableReels extends HTMLElement {
  connectedCallback() {
    this.cleanup?.abort();
    this.cleanup = new AbortController();
    const { signal } = this.cleanup;
    this.track = this.querySelector('.laasa-reels__track');
    this.cards = [...this.querySelectorAll('.laasa-reel')];
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.visibleCards = new Set();
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
        if (isIntersecting && intersectionRatio >= 0.55) this.visibleCards.add(target);
        else this.visibleCards.delete(target);
        this.syncPlayback(target);
      });
    }, { threshold: [0, 0.55, 1] });
    this.cards.forEach(card => {
      const video = card.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.playsInline = true;
      this.observer.observe(card);
      const play = card.querySelector('[data-reel-play]');
      const sound = card.querySelector('[data-reel-sound]');
      play?.addEventListener('click', () => {
        if (video.paused) {
          card.dataset.userPaused = 'false';
          video.play().catch(() => {});
        } else {
          card.dataset.userPaused = 'true';
          video.pause();
        }
      }, { signal });
      sound?.addEventListener('click', () => {
        const unmute = video.muted;
        // Keep only one audible reel at a time.
        this.cards.forEach(other => {
          const otherVideo = other.querySelector('video');
          if (otherVideo) otherVideo.muted = true;
          this.updateSound(other);
        });
        video.muted = !unmute;
        this.updateSound(card);
      }, { signal });
      const updatePlay = () => {
        if (!play) return;
        play.textContent = video.paused ? 'Play' : 'Pause';
        play.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        play.setAttribute('aria-pressed', String(!video.paused));
      };
      video.addEventListener('play', updatePlay, { signal });
      video.addEventListener('pause', updatePlay, { signal });
    });
    const section = this.closest('.laasa-reels');
    const scroll = direction => {
      const card = this.cards[0];
      if (!card || !this.track) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    };
    section?.querySelector('[data-reels-prev]')?.addEventListener('click', () => scroll(-1), { signal });
    section?.querySelector('[data-reels-next]')?.addEventListener('click', () => scroll(1), { signal });
    document.addEventListener('visibilitychange', () => this.cards.forEach(card => this.syncPlayback(card)), { signal });
    this.reducedMotion.addEventListener('change', () => this.cards.forEach(card => this.syncPlayback(card)), { signal });
    document.addEventListener('shopify:block:select', event => {
      const card = event.target.closest?.('.laasa-reel');
      if (card && this.contains(card)) card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, { signal });
  }
  syncPlayback(card) {
    const video = card.querySelector('video');
    if (!video) return;
    if (document.hidden || !this.visibleCards.has(card) || card.dataset.userPaused === 'true' || this.reducedMotion.matches) video.pause();
    else video.play().catch(() => {});
  }
  updateSound(card) {
    const video = card.querySelector('video');
    const button = card.querySelector('[data-reel-sound]');
    if (!video || !button) return;
    button.textContent = video.muted ? 'Sound off' : 'Sound on';
    button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    button.setAttribute('aria-pressed', String(!video.muted));
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.cleanup?.abort();
    this.querySelectorAll('video').forEach(video => video.pause());
  }
}
if (!customElements.get('laasa-shoppable-reels')) customElements.define('laasa-shoppable-reels', LaasaShoppableReels);
